import {useQuery} from '@github-ui/react-query'
import {createPersister} from '@github-ui/react-query-persister'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useMemo} from 'react'
import {
  FindingsQueryDataValidator,
  type Finding,
  type FindingsPayload,
  type FindingsQueryData,
  type Freshness,
  type MergeReadiness,
} from './types'

interface UseFindingsOptions {
  pullRequestBasePath: string
  prViewEnabled?: boolean
}

interface UseFindingsResult {
  findings: Finding[]
  freshness: Freshness
  modelsUrl: string | null
  mergeReadiness: MergeReadiness | null
  showFeedbackBanner: boolean
  feedbackBannerDismissPath: string | null
  isLoading: boolean
  error: string | null
  isNotFound: boolean
}

const DEFAULT_RETRY_AFTER_SECONDS = 10
const MIN_RETRY_AFTER_SECONDS = 1

const getRetryAfterMs = (
  response: Response | null,
  defaultRetryAfterSeconds: number = DEFAULT_RETRY_AFTER_SECONDS,
): number => {
  const retryAfterHeader = response?.headers.get('Retry-After')
  const parsedRetryAfter = retryAfterHeader === null ? Number.NaN : Number(retryAfterHeader)
  const retryAfterSeconds = Number.isFinite(parsedRetryAfter)
    ? Math.max(MIN_RETRY_AFTER_SECONDS, parsedRetryAfter)
    : defaultRetryAfterSeconds

  return retryAfterSeconds * 1000
}

export const FINDINGS_QUERY_KEY_PREFIX = 'code-review-findings' as const

export const findingsQueryKey = (pullRequestBasePath: string, prViewEnabled?: boolean) => {
  if (prViewEnabled !== undefined) {
    return [FINDINGS_QUERY_KEY_PREFIX, pullRequestBasePath, prViewEnabled] as const
  }
  return [FINDINGS_QUERY_KEY_PREFIX, pullRequestBasePath] as const
}

export const getRefetchInterval = (queryData: FindingsQueryData | undefined): number | false => {
  const freshness = queryData?.payload?.freshness
  if (freshness && freshness !== 'current') {
    return queryData?.retryAfterMs ?? DEFAULT_RETRY_AFTER_SECONDS * 1000
  }
  // Poll on 404 if the server sent Retry-After (findings not ready yet)
  if (queryData?.payload === null && queryData.retryAfterMs > 0) {
    return queryData.retryAfterMs
  }
  return false
}

export const useFindings = ({pullRequestBasePath, prViewEnabled = false}: UseFindingsOptions): UseFindingsResult => {
  const {sendAnalyticsEvent} = useAnalytics()

  const persister = useMemo(
    () =>
      createPersister({
        validator: FindingsQueryDataValidator,
        sendAnalyticsEvent,
      }),
    [sendAnalyticsEvent],
  )

  const {data, isLoading, error} = useQuery({
    queryKey: findingsQueryKey(pullRequestBasePath, prViewEnabled),
    queryFn: async (): Promise<FindingsQueryData> => {
      const viewQuery = prViewEnabled ? '?view=pr' : ''
      const response = await verifiedFetchJSON(`${pullRequestBasePath}/code_review_findings${viewQuery}`)
      if (response.status === 404) {
        return {payload: null, retryAfterMs: getRetryAfterMs(response, 0)}
      }
      if (!response.ok) {
        throw new Error(`Failed to fetch findings (${response.status})`)
      }
      const payload = (await response.json()) as FindingsPayload
      return {payload, retryAfterMs: getRetryAfterMs(response)}
    },
    refetchInterval: query => getRefetchInterval(query.state.data),
    persister,
  })

  const payload = data?.payload

  return {
    findings: payload?.findings ?? [],
    freshness: payload?.freshness ?? 'pending',
    modelsUrl: payload?.modelsUrl ?? null,
    mergeReadiness: payload?.mergeReadiness ?? null,
    showFeedbackBanner: payload?.showFeedbackBanner ?? false,
    feedbackBannerDismissPath: payload?.feedbackBannerDismissPath ?? null,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Failed to fetch findings') : null,
    isNotFound: data?.payload === null && data?.retryAfterMs === 0 && !isLoading && !error,
  }
}
